import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { usePromptContext, type KindFilter } from '../src/context/PromptContext';
import type { Folder } from '../types';
import {
  FolderIcon, StarIcon, TrashIcon, ChevronsLeftIcon, MenuIcon,
  PlusIcon, MoreVerticalIcon, ChevronRightIcon, ChevronDownIcon, UploadIcon,
  DownloadIcon, StampIcon, ChatSparkIcon, HistoryIcon, KindGlyph,
} from '../constants';
import usePromptActions from '../src/hooks/usePromptActions';
import { ITEM_KINDS, KIND_META } from '../utils/itemKinds';
import { BrandMark } from './Header';

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobile: boolean;
}

const countActive = (folder: Folder) => (folder.prompts || []).filter(p => !p.deletedAt).length;

const FolderTree: React.FC<{
  folders: Folder[];
  parentId: string | null;
  level?: number;
}> = React.memo(function FolderTree({ folders, parentId, level = 0 }) {
  const {
    selectedFolderId,
    setSelectedFolderId: onSelectFolder,
    setSelectedPromptId,
    setSelectedSpecialView,
    setActiveTag,
    closeSidebar,
  } = usePromptContext();
  const {
    handleMovePrompt: onMovePrompt,
    handleMoveFolder: onMoveFolder,
    handleOpenContextMenu: onOpenContextMenu,
  } = usePromptActions();
  const [openFolders, setOpenFolders] = useState<Record<string, boolean>>({});
  const [isDragOver, setIsDragOver] = useState<{ id: string; pos: 'into' | 'before' | 'after' } | null>(null);
  const springTimers = useRef<Record<string, number>>({});

  const toggleFolder = useCallback((id: string) => {
    setOpenFolders(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const clearSpringTimers = () => {
    Object.keys(springTimers.current).forEach(id => {
      window.clearTimeout(springTimers.current[id]);
      delete springTimers.current[id];
    });
  };

  useEffect(() => {
    return () => clearSpringTimers();
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>, folderId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    let pos: 'into' | 'before' | 'after' = 'into';
    if (y < rect.height * 0.25) pos = 'before';
    else if (y > rect.height * 0.75) pos = 'after';
    setIsDragOver({ id: folderId, pos });

    if (pos === 'into' && !openFolders[folderId] && !springTimers.current[folderId]) {
      springTimers.current[folderId] = window.setTimeout(() => {
        setOpenFolders(prev => ({ ...prev, [folderId]: true }));
        delete springTimers.current[folderId];
      }, 500);
    }
  }, [openFolders]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragOver(null);
    clearSpringTimers();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>, folderId: string, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    const currentDragOver = isDragOver;
    setIsDragOver(null);
    try {
      const data = e.dataTransfer.getData('application/json');
      if (data) {
        const draggedItem = JSON.parse(data);
        if (draggedItem.type === 'prompt' && draggedItem.id) {
          onMovePrompt(draggedItem.id, folderId);
        } else if (draggedItem.type === 'folder' && draggedItem.id && draggedItem.id !== folderId) {
          if (currentDragOver?.pos === 'before') onMoveFolder(draggedItem.id, parentId, index);
          else if (currentDragOver?.pos === 'after') onMoveFolder(draggedItem.id, parentId, index + 1);
          else onMoveFolder(draggedItem.id, folderId);
        }
      }
    } catch { /* ungültige Drag-Daten ignorieren */ }
    clearSpringTimers();
  }, [isDragOver, onMovePrompt, onMoveFolder, parentId]);

  const handleFolderDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, folder: Folder) => {
    if (folder.id === 'trash') { e.preventDefault(); return; }
    e.stopPropagation();
    e.dataTransfer.setData('application/json', JSON.stringify({ type: 'folder', id: folder.id, name: folder.name }));
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  return (
    <div role="group">
      {(folders || []).map((folder, index) => {
        const hasChildren = (folder.subFolders || []).length > 0;
        const isOpen = !!openFolders[folder.id];
        const isSelected = selectedFolderId === folder.id;
        const count = countActive(folder);
        const dropInto = isDragOver?.id === folder.id && isDragOver.pos === 'into';
        const indent = level * 14 + 6;

        return (
          <div key={folder.id} className="relative">
            {isDragOver?.id === folder.id && isDragOver.pos === 'before' && (
              <div className="absolute top-0 right-1 h-0.5 bg-kind-prompt rounded-full z-10" style={{ left: indent }} />
            )}
            <div
              draggable={folder.id !== 'trash'}
              onDragStart={(e) => handleFolderDragStart(e, folder)}
              onDragOver={(e) => handleDragOver(e, folder.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, folder.id, index)}
              onContextMenu={(e) => onOpenContextMenu(e, 'folder', folder.id, { name: folder.name })}
              className={`group relative flex items-center h-8 pr-1 rounded-md text-sm transition-colors ${
                isSelected ? 'bg-ink/[0.08] text-ink font-medium' : 'text-ink/80 hover:bg-ink/[0.05] hover:text-ink'
              } ${dropInto ? 'drop-active' : ''}`}
              style={{ paddingLeft: indent }}
              role="treeitem"
              aria-selected={isSelected}
              aria-expanded={hasChildren ? isOpen : undefined}
            >
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); if (hasChildren) toggleFolder(folder.id); }}
                className={`shrink-0 w-4 h-4 flex items-center justify-center rounded ${hasChildren ? 'text-graphite hover:text-ink' : 'invisible'}`}
                tabIndex={-1}
                aria-label={isOpen ? 'Unterordner einklappen' : 'Unterordner ausklappen'}
              >
                {isOpen ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
              </button>

              <button
                type="button"
                className="flex items-center flex-grow min-w-0 ml-1 h-full text-left"
                onClick={() => {
                  onSelectFolder(folder.id);
                  setSelectedSpecialView(null);
                  setActiveTag(null);
                  setSelectedPromptId(null);
                  closeSidebar?.();
                  if (hasChildren && !isOpen) toggleFolder(folder.id);
                }}
              >
                <FolderIcon className="h-4 w-4 shrink-0" style={{ color: folder.color || 'rgb(var(--graphite))' }} />
                <span className="ml-2 truncate">{folder.name}</span>
              </button>

              <span className={`ml-1 font-mono text-[11px] text-graphite tabular-nums group-hover:hidden group-focus-within:hidden ${count === 0 ? 'opacity-0' : ''}`}>
                {count}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); onOpenContextMenu(e, 'folder', folder.id, { name: folder.name }); }}
                className="hidden group-hover:flex group-focus-within:flex icon-btn !h-6 !w-6"
                aria-label={`Aktionen für Ordner ${folder.name}`}
                title="Neu anlegen, umbenennen, Farbe, löschen"
              >
                <MoreVerticalIcon className="h-4 w-4" />
              </button>

              {isDragOver?.id === folder.id && isDragOver.pos === 'after' && (
                <div className="absolute bottom-0 right-1 h-0.5 bg-kind-prompt rounded-full z-10" style={{ left: indent }} />
              )}
            </div>

            {isOpen && hasChildren && (
              <FolderTree folders={folder.subFolders || []} parentId={folder.id} level={level + 1} />
            )}
          </div>
        );
      })}
    </div>
  );
});

const NavRow: React.FC<{
  isActive: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count?: number;
  accentClass?: string;
}> = ({ isActive, onClick, icon, label, count, accentClass }) => (
  <button
    type="button"
    onClick={onClick}
    aria-current={isActive ? 'page' : undefined}
    className={`relative w-full flex items-center gap-2.5 h-8 pl-2.5 pr-2 rounded-md text-sm transition-colors ${
      isActive ? 'bg-ink/[0.08] text-ink font-medium' : 'text-ink/80 hover:bg-ink/[0.05] hover:text-ink'
    }`}
  >
    {isActive && accentClass && <span className={`absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full ${accentClass}`} aria-hidden="true" />}
    <span className="shrink-0 w-4 flex justify-center">{icon}</span>
    <span className="truncate">{label}</span>
    {count !== undefined && (
      <span className="ml-auto font-mono text-[11px] text-graphite tabular-nums">{count}</span>
    )}
  </button>
);

const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
  <section className="mb-5">
    <div className="flex items-center justify-between h-6 px-2.5 mb-1">
      <h3 className="label-xs">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

const TAG_PREVIEW_LIMIT = 14;

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggleCollapse, isMobile }) => {
  const {
    fileSystem,
    activeTag,
    setActiveTag,
    setSelectedFolderId,
    setSelectedPromptId,
    selectedSpecialView,
    setSelectedSpecialView,
    selectedFolderId,
    setModalState,
    closeSidebar,
    kindFilter,
    setKindFilter,
  } = usePromptContext();

  const actions = usePromptActions();
  const { handleImport, handleExport, handleMoveFolder, allTags, libraryCounts, trashPrompts } = actions;
  const [showAllTags, setShowAllTags] = useState(false);

  const go = useCallback((view: string | null, opts: { kind?: KindFilter; folderId?: string | null; tag?: string | null } = {}) => {
    setSelectedPromptId(null);
    setSelectedFolderId(opts.folderId ?? null);
    setActiveTag(opts.tag ?? null);
    setSelectedSpecialView(view);
    if (opts.kind !== undefined) {
      setKindFilter(opts.kind);
    } else if (view === 'trash' || view === 'recent' || view === 'favorites') {
      setKindFilter('all');
    }
    closeSidebar?.();
  }, [setSelectedPromptId, setSelectedFolderId, setActiveTag, setSelectedSpecialView, setKindFilter, closeSidebar]);

  const isLibraryView = selectedSpecialView === 'all' && !selectedFolderId && !activeTag;

  // Tags nach Häufigkeit sortiert, damit die wichtigsten oben stehen
  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    actions.allPrompts.forEach(p => {
      if (p.deletedAt) return;
      (p.tags || []).forEach(t => { counts[t] = (counts[t] || 0) + 1; });
    });
    return counts;
  }, [actions.allPrompts]);
  const sortedTags = useMemo(
    () => [...allTags].sort((a, b) => (tagCounts[b] || 0) - (tagCounts[a] || 0) || a.localeCompare(b, 'de')),
    [allTags, tagCounts],
  );
  const visibleTags = showAllTags ? sortedTags : sortedTags.slice(0, TAG_PREVIEW_LIMIT);

  const libraryRows: { kind: KindFilter; label: string; icon: React.ReactNode; accent?: string }[] = [
    { kind: 'all', label: 'Alles', icon: <span className="flex flex-col gap-[3px]" aria-hidden="true"><span className="block h-[2px] w-3.5 rounded bg-kind-prompt" /><span className="block h-[2px] w-2.5 rounded bg-kind-skill" /><span className="block h-[2px] w-3 rounded bg-kind-workflow" /><span className="block h-[2px] w-2 rounded bg-kind-gem" /></span>, accent: 'bg-ink' },
    ...ITEM_KINDS.map(kind => ({
      kind,
      label: KIND_META[kind].plural,
      icon: <span className={KIND_META[kind].text}><KindGlyph kind={kind} /></span>,
      accent: KIND_META[kind].bg,
    })),
  ];

  const tools = [
    { view: 'templates', label: 'Vorlagen', icon: <StampIcon className="h-4 w-4" /> },
    { view: 'generator', label: 'Prompt-Generator', icon: <ChatSparkIcon className="h-4 w-4" /> },
  ];

  if (isCollapsed && !isMobile) {
    const railBtn = (active: boolean) => `icon-btn ${active ? '!bg-ink/[0.09] !text-ink' : ''}`;
    return (
      <nav className="h-full bg-sheet border-r border-rule py-3 flex flex-col items-center gap-1" aria-label="Navigation (eingeklappt)">
        <button onClick={onToggleCollapse} className="icon-btn mb-2" aria-label="Seitenleiste ausklappen" title="Seitenleiste ausklappen">
          <MenuIcon className="h-5 w-5" />
        </button>
        {libraryRows.map(row => (
          <button
            key={row.kind}
            onClick={() => go('all', { kind: row.kind })}
            className={railBtn(isLibraryView && kindFilter === row.kind)}
            aria-label={row.label}
            title={row.label}
          >
            {row.icon}
          </button>
        ))}
        <div className="w-6 border-t border-rule my-2" aria-hidden="true" />
        <button onClick={() => go('recent')} className={railBtn(selectedSpecialView === 'recent')} aria-label="Zuletzt verwendet" title="Zuletzt verwendet"><HistoryIcon className="h-4 w-4" /></button>
        <button onClick={() => go('favorites')} className={railBtn(selectedSpecialView === 'favorites')} aria-label="Favoriten" title="Favoriten"><StarIcon className="h-4 w-4" /></button>
        <div className="mt-auto flex flex-col items-center gap-1">
          {tools.map(t => (
            <button key={t.view} onClick={() => go(t.view)} className={railBtn(selectedSpecialView === t.view)} aria-label={t.label} title={t.label}>{t.icon}</button>
          ))}
          <button onClick={() => go('trash')} className={railBtn(selectedSpecialView === 'trash')} aria-label="Papierkorb" title="Papierkorb"><TrashIcon className="h-4 w-4" /></button>
        </div>
      </nav>
    );
  }

  return (
    <nav className="h-full flex flex-col bg-sheet border-r border-rule" aria-label="Navigation">
      {/* Marke */}
      <div className="flex items-center justify-between h-14 px-3 shrink-0 border-b border-rule">
        <div className="flex items-center gap-2.5 min-w-0">
          <BrandMark className="h-7 w-7 shrink-0" />
          <div className="leading-none min-w-0">
            <div className="font-display text-[17px] font-semibold text-ink tracking-[-0.01em] truncate">Prompt Manager</div>
            <div className="font-mono text-[10px] text-graphite mt-0.5 uppercase tracking-[0.12em]">Pro · Bibliothek</div>
          </div>
        </div>
        {!isMobile && (
          <button onClick={onToggleCollapse} className="icon-btn" aria-label="Seitenleiste einklappen" title="Seitenleiste einklappen">
            <ChevronsLeftIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex-grow overflow-y-auto scrollbar-thin px-2 pt-4">
        <Section title="Bibliothek">
          <div className="space-y-px">
            {libraryRows.map(row => (
              <NavRow
                key={row.kind}
                isActive={isLibraryView && kindFilter === row.kind}
                onClick={() => go('all', { kind: row.kind })}
                icon={row.icon}
                label={row.label}
                count={libraryCounts[row.kind]}
                accentClass={row.accent}
              />
            ))}
          </div>
        </Section>

        <Section title="Ansichten">
          <div className="space-y-px">
            <NavRow isActive={selectedSpecialView === 'recent'} onClick={() => go('recent')} icon={<HistoryIcon className="h-4 w-4 text-graphite" />} label="Zuletzt verwendet" />
            <NavRow isActive={selectedSpecialView === 'favorites'} onClick={() => go('favorites')} icon={<StarIcon className="h-4 w-4 text-amber-600 dark:text-amber-400" />} label="Favoriten" />
          </div>
        </Section>

        <Section
          title="Ordner"
          action={
            <button
              onClick={() => setModalState({ type: 'create-folder', parentId: null })}
              className="icon-btn !h-6 !w-6"
              aria-label="Neuen Ordner anlegen"
              title="Neuen Ordner anlegen"
            >
              <PlusIcon className="h-4 w-4" />
            </button>
          }
        >
          <div
            role="tree"
            aria-label="Ordner"
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              try {
                const data = e.dataTransfer.getData('application/json');
                if (data) {
                  const item = JSON.parse(data);
                  if (item.type === 'folder' && item.id) {
                    handleMoveFolder(item.id, null);
                  }
                }
              } catch { /* ungültige Drag-Daten ignorieren */ }
            }}
          >
            <FolderTree folders={(fileSystem || []).filter(f => f.id !== 'trash')} parentId={null} />
          </div>
          {(fileSystem || []).filter(f => f.id !== 'trash').length === 0 && (
            <button
              onClick={() => setModalState({ type: 'create-folder', parentId: null })}
              className="w-full mx-0 mt-1 px-2.5 py-3 rounded-md border border-dashed border-rule text-xs text-graphite hover:text-ink hover:border-graphite/60 text-left"
            >
              Ersten Ordner anlegen
            </button>
          )}
        </Section>

        {sortedTags.length > 0 && (
          <Section title="Tags">
            <div className="flex flex-wrap gap-1 px-2">
              {visibleTags.map(tag => {
                const isActive = activeTag === tag;
                return (
                  <button
                    key={tag}
                    onClick={() => (isActive ? go('all') : go('all', { tag }))}
                    aria-pressed={isActive}
                    className={`inline-flex items-center gap-1 h-6 px-2 rounded font-mono text-[11.5px] transition-colors ${
                      isActive ? 'bg-ink text-sheet' : 'bg-ink/[0.05] text-ink/80 hover:bg-ink/[0.1] hover:text-ink'
                    }`}
                  >
                    {tag}
                    <span className={isActive ? 'text-sheet/70' : 'text-graphite'}>{tagCounts[tag]}</span>
                  </button>
                );
              })}
            </div>
            {sortedTags.length > TAG_PREVIEW_LIMIT && (
              <button onClick={() => setShowAllTags(v => !v)} className="mt-2 ml-2.5 text-xs text-graphite hover:text-ink underline-offset-2 hover:underline">
                {showAllTags ? 'Weniger anzeigen' : `Alle ${sortedTags.length} Tags anzeigen`}
              </button>
            )}
          </Section>
        )}
      </div>

      {/* Werkzeuge */}
      <div className="shrink-0 border-t border-rule px-2 py-2 space-y-px">
        {tools.map(t => (
          <NavRow key={t.view} isActive={selectedSpecialView === t.view} onClick={() => go(t.view)} icon={<span className="text-graphite">{t.icon}</span>} label={t.label} />
        ))}
        <NavRow
          isActive={selectedSpecialView === 'trash'}
          onClick={() => go('trash')}
          icon={<TrashIcon className="h-4 w-4 text-graphite" />}
          label="Papierkorb"
          count={trashPrompts.length || undefined}
        />
        <div className="flex gap-1 pt-1.5">
          <button onClick={handleImport} className="btn btn-ghost btn-sm flex-1 !text-xs !min-h-[30px]" title="Sicherung (JSON) einspielen">
            <UploadIcon className="h-3.5 w-3.5" /> Import
          </button>
          <button onClick={handleExport} className="btn btn-ghost btn-sm flex-1 !text-xs !min-h-[30px]" title="Sicherung oder Liste exportieren (Strg+Umschalt+E)">
            <DownloadIcon className="h-3.5 w-3.5" /> Export
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Sidebar;
